// `settings`: the company singleton, Swift `CompanySettings` (GroundTeam/Models/CompanySettings.swift). Its envelope id is
// SETTINGS_ID and its payload has no `id` key. iOS decodes it over `CompanySettings()` (SyncReplica.merge), so a
// missing key takes the property default. The server reads `companyName` and `repsSeeOnlyOwnRecords` from it
// (M5 sync_items_after_settings; a missing flag counts as true), so both are always written.

import {
  DISTANCE_UNITS,
  MAP_COLORIZES,
  RECORD_TYPES,
  type DistanceUnit,
  type MapColorize,
  type RecordType,
} from '../enums';
import type { JsonObject } from '../wire';
import {
  BOOL,
  DOUBLE,
  INT,
  STRING,
  createModel,
  decodeModel,
  enumOf,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface SettingsPayload extends Passthrough {
  companyName: string;
  currencyCode: string;
  distanceUnit: DistanceUnit;
  verificationRadiusMeters: number;
  workdayStartHour: number;
  workdayEndHour: number;
  requireNotesOnVisit: boolean;
  repsSeeOnlyOwnRecords: boolean;
  allowRepsToCreateTerritories: boolean;
  trackRepLocation: boolean;
  homeTipsEnabled: boolean;
  defaultMapColorize: MapColorize;
  onboardingCompleted: boolean;
  seededSampleData: boolean;
  leadMachineEnabled: boolean;
  autoVisitPromptEnabled: boolean;
  autoVisitRadiusMeters: number;
  /** Int. */
  autoVisitDwellMinutes: number;
  defaultRecordType: RecordType;
}

/** `CompanySettings.defaultVerificationRadiusMeters`: also the radius for visits logged without a captured one. */
export const DEFAULT_VERIFICATION_RADIUS_METERS = 100;

/** Stored properties of CompanySettings in declaration order (none is local-only). */
export const SETTINGS_KEYS = [
  'companyName',
  'currencyCode',
  'distanceUnit',
  'verificationRadiusMeters',
  'workdayStartHour',
  'workdayEndHour',
  'requireNotesOnVisit',
  'repsSeeOnlyOwnRecords',
  'allowRepsToCreateTerritories',
  'trackRepLocation',
  'homeTipsEnabled',
  'defaultMapColorize',
  'onboardingCompleted',
  'seededSampleData',
  'leadMachineEnabled',
  'autoVisitPromptEnabled',
  'autoVisitRadiusMeters',
  'autoVisitDwellMinutes',
  'defaultRecordType',
] as const satisfies readonly (keyof Known<SettingsPayload>)[];

/** `CompanySettings()` property defaults (also the decoding template). */
const SETTINGS_DEFAULTS: Known<SettingsPayload> = {
  companyName: 'Ground Team',
  currencyCode: 'USD',
  distanceUnit: 'miles',
  verificationRadiusMeters: DEFAULT_VERIFICATION_RADIUS_METERS,
  workdayStartHour: 8,
  workdayEndHour: 18,
  requireNotesOnVisit: false,
  repsSeeOnlyOwnRecords: true,
  allowRepsToCreateTerritories: false,
  trackRepLocation: true,
  homeTipsEnabled: true,
  defaultMapColorize: 'visitResult',
  onboardingCompleted: false,
  seededSampleData: false,
  leadMachineEnabled: true,
  autoVisitPromptEnabled: true,
  autoVisitRadiusMeters: 60,
  autoVisitDwellMinutes: 3,
  defaultRecordType: 'lead',
};

const d = SETTINGS_DEFAULTS;
export const SETTINGS_FIELDS: FieldMap<SettingsPayload> = {
  companyName: field.req(STRING, () => d.companyName),
  currencyCode: field.req(STRING, () => d.currencyCode),
  distanceUnit: field.req(enumOf('DistanceUnit', DISTANCE_UNITS), () => d.distanceUnit),
  verificationRadiusMeters: field.req(DOUBLE, () => d.verificationRadiusMeters),
  workdayStartHour: field.req(INT, () => d.workdayStartHour),
  workdayEndHour: field.req(INT, () => d.workdayEndHour),
  requireNotesOnVisit: field.req(BOOL, () => d.requireNotesOnVisit),
  repsSeeOnlyOwnRecords: field.req(BOOL, () => d.repsSeeOnlyOwnRecords),
  allowRepsToCreateTerritories: field.req(BOOL, () => d.allowRepsToCreateTerritories),
  trackRepLocation: field.req(BOOL, () => d.trackRepLocation),
  homeTipsEnabled: field.req(BOOL, () => d.homeTipsEnabled),
  defaultMapColorize: field.req(enumOf('MapColorize', MAP_COLORIZES), () => d.defaultMapColorize),
  onboardingCompleted: field.req(BOOL, () => d.onboardingCompleted),
  seededSampleData: field.req(BOOL, () => d.seededSampleData),
  leadMachineEnabled: field.req(BOOL, () => d.leadMachineEnabled),
  autoVisitPromptEnabled: field.req(BOOL, () => d.autoVisitPromptEnabled),
  autoVisitRadiusMeters: field.req(DOUBLE, () => d.autoVisitRadiusMeters),
  autoVisitDwellMinutes: field.req(INT, () => d.autoVisitDwellMinutes),
  defaultRecordType: field.req(enumOf('RecordType', RECORD_TYPES), () => d.defaultRecordType),
};

/** Decodes the settings payload (no `id`; the envelope id is SETTINGS_ID and is not compared). */
export function decodeSettings(raw: JsonObject): Decoded<SettingsPayload> {
  return decodeModel(SETTINGS_FIELDS, raw, { hasId: false });
}

export type SettingsInit = Partial<Known<SettingsPayload>>;

/**
 * Every key with its CompanySettings default. A company created on the web passes `onboardingCompleted: true`
 * (WEB_PLAN §2.3) and its name.
 */
export function newSettings(init: SettingsInit = {}): SettingsPayload {
  return createModel(SETTINGS_FIELDS, { ...SETTINGS_DEFAULTS }, init, 'settings', { hasId: false });
}
